import { useState, useEffect, useRef, useCallback, type FormEvent, type DragEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import './ProjetoWizard.css';

interface StepErrors {
  [key: string]: string;
}

interface ClienteOption {
  id: string;
  nome: string;
  email: string;
}

interface EtapaCatalogo {
  id: string;
  nome: string;
  descricao: string;
}

interface ParticipanteOption {
  id: string;
  nome: string;
  email: string;
  tipo: string;
}

interface ViaCepResponse {
  cep: string;
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
  erro?: boolean;
}

const TOTAL_STEPS = 3;

function ProjetoWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tipo = (searchParams.get('tipo') || 'obra') as 'projeto' | 'obra';
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<StepErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  // Data loaded from API
  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [etapasCatalogo, setEtapasCatalogo] = useState<EtapaCatalogo[]>([]);
  const [participantes, setParticipantes] = useState<ParticipanteOption[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // Step 1: Project data
  const [nome, setNome] = useState('');
  const [cep, setCep] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');
  const [endereco, setEndereco] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataConclusao, setDataConclusao] = useState('');

  // Drag and drop state for Step 3
  const [draggedEtapaId, setDraggedEtapaId] = useState<string | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);
  const [dragOverPanel, setDragOverPanel] = useState<'available' | 'selected' | null>(null);
  const dragCounterRef = useRef(0);

  // Step 2: Titular
  const [titularId, setTitularId] = useState<string>('');

  // Step 3: Stages
  const [selectedEtapas, setSelectedEtapas] = useState<string[]>([]);

  // Step 4: Participants
  const [visualizadores, setVisualizadores] = useState<string[]>([]);
  const [supervisao, setSupervisao] = useState<string[]>([]);
  const [controle, setControle] = useState<string[]>([]);

  useEffect(() => {
    async function loadData() {
      try {
        setLoadingData(true);
        const [clientesData, etapasData, participantesData] = await Promise.all([
          api.get('/usuarios?tipo=Cliente&ativo=true'),
          api.get(`/tipos-etapa?categoria=${tipo}`),
          api.get('/usuarios?ativo=true'),
        ]);
        setClientes(clientesData);
        // Filter only active stage types
        setEtapasCatalogo(
          (etapasData as EtapaCatalogo[]).filter((e: EtapaCatalogo & { ativo?: boolean }) => e.ativo !== false)
        );
        setParticipantes(participantesData);
      } catch (err: unknown) {
        setApiError(err instanceof Error ? err.message : 'Erro ao carregar dados');
      } finally {
        setLoadingData(false);
      }
    }
    loadData();
  }, [tipo]);

  // CEP search
  useEffect(() => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      setCepLoading(true);
      setCepError('');
      fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
        .then((res) => res.json())
        .then((data: ViaCepResponse) => {
          if (data.erro) {
            setCepError('CEP não encontrado');
          } else {
            const parts = [data.logradouro, data.bairro].filter(Boolean).join(', ');
            const city = `${data.localidade}/${data.uf}`;
            setEndereco(parts ? `${parts} - ${city}` : city);
          }
        })
        .catch(() => {
          setCepError('Erro ao buscar CEP');
        })
        .finally(() => {
          setCepLoading(false);
        });
    } else {
      setCepError('');
    }
  }, [cep]);

  const validateStep = (step: number): boolean => {
    const newErrors: StepErrors = {};

    switch (step) {
      case 1:
        if (!nome.trim()) newErrors.nome = 'Nome é obrigatório';
        if (!endereco.trim()) newErrors.endereco = 'Endereço é obrigatório';
        if (!dataInicio) newErrors.dataInicio = 'Data de início é obrigatória';
        if (!dataConclusao) newErrors.dataConclusao = 'Data de conclusão é obrigatória';
        if (dataInicio && dataConclusao && dataInicio >= dataConclusao) {
          newErrors.dataConclusao = 'Data de conclusão deve ser posterior à data de início';
        }
        break;
      case 2:
        if (!titularId) newErrors.titular = `Selecione um titular para ${tipo === 'projeto' ? 'o projeto' : 'a obra'}`;
        break;
      case 3:
        if (selectedEtapas.length === 0) newErrors.etapas = 'Selecione pelo menos uma etapa';
        break;
      case 4:
        // Optional: participants are not strictly required
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goNext = () => {
    if (validateStep(currentStep) && currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goPrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Only submit on the last step
    if (currentStep !== TOTAL_STEPS) return;
    if (!validateStep(currentStep)) return;

    setSubmitting(true);
    setApiError('');

    try {
      // Build associations array
      const associacoes: { usuarioId: string; papel: string }[] = [];
      visualizadores.forEach((id) => associacoes.push({ usuarioId: id, papel: 'visualizador' }));
      supervisao.forEach((id) => associacoes.push({ usuarioId: id, papel: 'supervisao' }));
      controle.forEach((id) => associacoes.push({ usuarioId: id, papel: 'controle' }));

      // Build etapas array with order
      const etapas = selectedEtapas.map((tipoEtapaId, index) => ({
        tipoEtapaId,
        ordem: index + 1,
      }));

      await api.post('/empreendimentos', {
        nome,
        endereco,
        dataInicio,
        dataPrevistaConclusao: dataConclusao,
        titularId,
        tipo,
        etapas,
      });

      // Redirect to painel where the new item will be visible in the correct tab
      navigate('/painel');
    } catch (err: unknown) {
      setApiError(err instanceof Error ? err.message : `Erro ao criar ${tipo === 'projeto' ? 'projeto' : 'obra'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleParticipant = (
    id: string,
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setList(list.includes(id) ? list.filter((p) => p !== id) : [...list, id]);
  };

  // Drag and Drop handlers for Step 3
  const handleDragStart = useCallback((e: DragEvent<HTMLDivElement>, etapaId: string) => {
    setDraggedEtapaId(etapaId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', etapaId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedEtapaId(null);
    setDragOverTarget(null);
    setDragOverPanel(null);
    dragCounterRef.current = 0;
  }, []);

  const handlePanelDragEnter = useCallback((panel: 'available' | 'selected') => {
    dragCounterRef.current++;
    setDragOverPanel(panel);
  }, []);

  const handlePanelDragLeave = useCallback(() => {
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      setDragOverPanel(null);
      dragCounterRef.current = 0;
    }
  }, []);

  const handlePanelDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDropOnAvailable = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const etapaId = e.dataTransfer.getData('text/plain');
    if (etapaId && selectedEtapas.includes(etapaId)) {
      setSelectedEtapas((prev) => prev.filter((id) => id !== etapaId));
    }
    setDraggedEtapaId(null);
    setDragOverTarget(null);
    setDragOverPanel(null);
    dragCounterRef.current = 0;
  }, [selectedEtapas]);

  const handleDropOnSelected = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const etapaId = e.dataTransfer.getData('text/plain');
    if (etapaId && !selectedEtapas.includes(etapaId)) {
      setSelectedEtapas((prev) => [...prev, etapaId]);
    }
    setDraggedEtapaId(null);
    setDragOverTarget(null);
    setDragOverPanel(null);
    dragCounterRef.current = 0;
  }, [selectedEtapas]);

  const handleDropOnItem = useCallback((e: DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const etapaId = e.dataTransfer.getData('text/plain');
    if (!etapaId) return;

    setSelectedEtapas((prev) => {
      let newList = [...prev];
      // If dragged from available → add first, then reorder
      if (!newList.includes(etapaId)) {
        newList.push(etapaId);
      }
      // Reorder: remove from current position, insert at target position
      const fromIndex = newList.indexOf(etapaId);
      const toIndex = newList.indexOf(targetId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return newList;
      newList.splice(fromIndex, 1);
      newList.splice(toIndex, 0, etapaId);
      return newList;
    });

    setDraggedEtapaId(null);
    setDragOverTarget(null);
    setDragOverPanel(null);
    dragCounterRef.current = 0;
  }, []);

  const stepLabels = ['Dados', 'Titular', 'Etapas'];

  if (loadingData) {
    return (
      <div className="projeto-wizard">
        <div className="projeto-wizard-container">
          <p style={{ color: '#c9a96e', textAlign: 'center', marginTop: '2rem' }}>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="projeto-wizard">
      <div className="projeto-wizard-container">
        <header className="projeto-wizard-header">
          <h1>{tipo === 'projeto' ? 'Novo Projeto' : 'Nova Obra'}</h1>
          <p className="projeto-wizard-subtitle">
            Preencha as informações para criar {tipo === 'projeto' ? 'um novo projeto' : 'uma nova obra'}
          </p>
        </header>

        {apiError && (
          <p style={{ color: '#ff6b6b', textAlign: 'center', margin: '1rem 0' }}>{apiError}</p>
        )}

        {/* Steps Indicator */}
        <div className="wizard-steps-indicator" aria-label="Etapas do assistente">
          {stepLabels.map((label, index) => {
            const stepNum = index + 1;
            const isActive = stepNum === currentStep;
            const isCompleted = stepNum < currentStep;
            return (
              <div
                key={label}
                className={`wizard-step-dot ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              >
                <div className="wizard-dot-circle">{isCompleted ? '✓' : stepNum}</div>
                <span className="wizard-step-label">{label}</span>
              </div>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Step 1: Project Data */}
          {currentStep === 1 && (
            <div className="wizard-step-content">
              <h2>Dados {tipo === 'projeto' ? 'do Projeto' : 'da Obra'}</h2>

              <div className="form-group">
                <label htmlFor="wiz-nome">Nome {tipo === 'projeto' ? 'do Projeto' : 'da Obra'}</label>
                <input
                  id="wiz-nome"
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex: Residência Família Silva"
                  className={errors.nome ? 'error' : ''}
                />
                <span className="form-error">{errors.nome || ''}</span>
              </div>

              <div className="form-group">
                <label htmlFor="wiz-cep">CEP</label>
                <div className="cep-input-wrapper">
                  <input
                    id="wiz-cep"
                    type="text"
                    value={cep}
                    onChange={(e) => setCep(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    placeholder="Ex: 89010001"
                    maxLength={8}
                    className={cepError ? 'error' : ''}
                  />
                  {cepLoading && <span className="cep-loading">Buscando...</span>}
                </div>
                <span className="form-error">{cepError || ''}</span>
              </div>

              <div className="form-group">
                <label htmlFor="wiz-endereco">Endereço</label>
                <input
                  id="wiz-endereco"
                  type="text"
                  value={endereco}
                  onChange={(e) => setEndereco(e.target.value)}
                  placeholder="Ex: Rua das Flores, 123 - Blumenau/SC"
                  className={errors.endereco ? 'error' : ''}
                />
                <span className="form-error">{errors.endereco || ''}</span>
              </div>

              <div className="form-group">
                <label htmlFor="wiz-data-inicio">Data de Início</label>
                <input
                  id="wiz-data-inicio"
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className={errors.dataInicio ? 'error' : ''}
                />
                <span className="form-error">{errors.dataInicio || ''}</span>
              </div>

              <div className="form-group">
                <label htmlFor="wiz-data-conclusao">Data Prevista de Conclusão</label>
                <input
                  id="wiz-data-conclusao"
                  type="date"
                  value={dataConclusao}
                  onChange={(e) => setDataConclusao(e.target.value)}
                  className={errors.dataConclusao ? 'error' : ''}
                />
                <span className="form-error">{errors.dataConclusao || ''}</span>
              </div>
            </div>
          )}

          {/* Step 2: Select Titular */}
          {currentStep === 2 && (
            <div className="wizard-step-content">
              <h2>Selecionar Titular (Cliente)</h2>
              <p style={{ color: '#999', fontSize: '0.85rem', marginBottom: '1rem' }}>
                Selecione o cliente titular {tipo === 'projeto' ? 'do projeto' : 'da obra'}
              </p>

              {errors.titular && <span className="form-error">{errors.titular}</span>}

              <div className="wizard-selectable-list" role="radiogroup" aria-label="Lista de clientes">
                {clientes.map((cliente) => (
                  <label
                    key={cliente.id}
                    className={`wizard-selectable-item ${titularId === cliente.id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="titular"
                      value={cliente.id}
                      checked={titularId === cliente.id}
                      onChange={() => setTitularId(cliente.id)}
                    />
                    <div className="wizard-selectable-item-info">
                      <div className="wizard-selectable-item-name">{cliente.nome}</div>
                      <div className="wizard-selectable-item-detail">{cliente.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Select & Sort Stages via Drag and Drop */}
          {currentStep === 3 && (
            <div className="wizard-step-content">
              <h2>Selecionar Etapas</h2>
              <p style={{ color: '#999', fontSize: '0.85rem', marginBottom: '1rem' }}>
                Arraste as etapas da esquerda para a direita para selecionar. Reordene arrastando dentro da lista.
              </p>

              {errors.etapas && <span className="form-error">{errors.etapas}</span>}

              <div className="wizard-dnd-container">
                {/* Available panel */}
                <div
                  className={`wizard-dnd-panel ${dragOverPanel === 'available' ? 'drag-over' : ''}`}
                  onDragEnter={() => handlePanelDragEnter('available')}
                  onDragLeave={handlePanelDragLeave}
                  onDragOver={handlePanelDragOver}
                  onDrop={handleDropOnAvailable}
                >
                  <h3 className="wizard-dnd-panel-title">Disponíveis</h3>
                  <div className="wizard-dnd-list">
                    {etapasCatalogo
                      .filter((e) => !selectedEtapas.includes(e.id))
                      .map((etapa) => (
                        <div
                          key={etapa.id}
                          className={`wizard-dnd-item ${draggedEtapaId === etapa.id ? 'dragging' : ''}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, etapa.id)}
                          onDragEnd={handleDragEnd}
                        >
                          <span className="wizard-dnd-handle">☰</span>
                          <div className="wizard-dnd-item-info">
                            <div className="wizard-dnd-item-name">{etapa.nome}</div>
                            <div className="wizard-dnd-item-detail">{etapa.descricao}</div>
                          </div>
                        </div>
                      ))}
                    {etapasCatalogo.filter((e) => !selectedEtapas.includes(e.id)).length === 0 && (
                      <p className="wizard-dnd-empty">Todas as etapas foram selecionadas</p>
                    )}
                  </div>
                </div>

                {/* Selected panel */}
                <div
                  className={`wizard-dnd-panel ${dragOverPanel === 'selected' ? 'drag-over' : ''}`}
                  onDragEnter={() => handlePanelDragEnter('selected')}
                  onDragLeave={handlePanelDragLeave}
                  onDragOver={handlePanelDragOver}
                  onDrop={handleDropOnSelected}
                >
                  <h3 className="wizard-dnd-panel-title">Selecionadas (ordem)</h3>
                  <div className="wizard-dnd-list">
                    {selectedEtapas.map((etapaId, index) => {
                      const etapa = etapasCatalogo.find((e) => e.id === etapaId);
                      if (!etapa) return null;
                      return (
                        <div
                          key={etapa.id}
                          className={`wizard-dnd-item selected ${draggedEtapaId === etapa.id ? 'dragging' : ''} ${dragOverTarget === etapa.id ? 'drop-target' : ''}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, etapa.id)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragOverTarget(etapa.id);
                          }}
                          onDragLeave={() => setDragOverTarget(null)}
                          onDrop={(e) => handleDropOnItem(e, etapa.id)}
                        >
                          <span className="wizard-dnd-order">{index + 1}.</span>
                          <span className="wizard-dnd-handle">☰</span>
                          <div className="wizard-dnd-item-info">
                            <div className="wizard-dnd-item-name">{etapa.nome}</div>
                          </div>
                        </div>
                      );
                    })}
                    {selectedEtapas.length === 0 && (
                      <p className="wizard-dnd-empty">Arraste etapas para cá</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Participants */}
          {currentStep === 4 && (
            <div className="wizard-step-content">
              <h2>Associar Participantes</h2>
              <p style={{ color: '#999', fontSize: '0.85rem', marginBottom: '1rem' }}>
                Defina os papéis dos participantes no projeto
              </p>

              <div className="wizard-participants-section">
                <h3>Clientes Complementares</h3>
                <div className="wizard-selectable-list">
                  {participantes.map((p) => (
                    <label
                      key={p.id}
                      className={`wizard-selectable-item ${visualizadores.includes(p.id) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={visualizadores.includes(p.id)}
                        onChange={() => toggleParticipant(p.id, visualizadores, setVisualizadores)}
                      />
                      <div className="wizard-selectable-item-info">
                        <div className="wizard-selectable-item-name">{p.nome}</div>
                        <div className="wizard-selectable-item-detail">{p.tipo} — {p.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="wizard-participants-section">
                <h3>Arquitetos</h3>
                <div className="wizard-selectable-list">
                  {participantes
                    .filter((p) => p.tipo === 'Arquiteto')
                    .map((p) => (
                      <label
                        key={p.id}
                        className={`wizard-selectable-item ${supervisao.includes(p.id) ? 'selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={supervisao.includes(p.id)}
                          onChange={() => toggleParticipant(p.id, supervisao, setSupervisao)}
                        />
                        <div className="wizard-selectable-item-info">
                          <div className="wizard-selectable-item-name">{p.nome}</div>
                          <div className="wizard-selectable-item-detail">{p.email}</div>
                        </div>
                      </label>
                    ))}
                </div>
              </div>

              <div className="wizard-participants-section">
                <h3>Assistentes</h3>
                <div className="wizard-selectable-list">
                  {participantes
                    .filter((p) => p.tipo === 'Assistente' || p.tipo === 'Arquiteto')
                    .map((p) => (
                      <label
                        key={p.id}
                        className={`wizard-selectable-item ${controle.includes(p.id) ? 'selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={controle.includes(p.id)}
                          onChange={() => toggleParticipant(p.id, controle, setControle)}
                        />
                        <div className="wizard-selectable-item-info">
                          <div className="wizard-selectable-item-name">{p.nome}</div>
                          <div className="wizard-selectable-item-detail">{p.tipo} — {p.email}</div>
                        </div>
                      </label>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="wizard-navigation">
            <button
              type="button"
              className="btn-secondary"
              onClick={goPrev}
              disabled={currentStep === 1}
              style={{ visibility: currentStep === 1 ? 'hidden' : 'visible' }}
            >
              ← Anterior
            </button>

            {currentStep < TOTAL_STEPS ? (
              <button type="button" className="btn-primary" onClick={goNext}>
                Próximo →
              </button>
            ) : (
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Criando...' : tipo === 'projeto' ? 'Criar Projeto ✓' : 'Criar Obra ✓'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProjetoWizard;
